import { useEffect, useState } from "react";
import { Bell, BellOff, X, Check } from "lucide-react";
import {
  isPushSupported, getPermissionStatus,
  subscribeUserToPush, unsubscribeUserFromPush,
  playNotificationSound, attachPushBridge,
} from "@/lib/push";
import { api } from "@/api/client";
import { useI18n } from "@/contexts/I18nContext";
import { useAuth } from "@/contexts/AuthContext";

const DISMISS_KEY = "vta:push:dismissed_at";
const DISMISS_TTL_MS = 7 * 24 * 60 * 60 * 1000; // re-ask after 7 days

/**
 * PushPermissionBanner — WhatsApp-style ask-once banner for enabling push.
 * Renders a gold pill at the bottom of the app shell for internal users when:
 *   - Browser supports Push
 *   - Permission status is 'default'
 *   - User hasn't dismissed in the last 7 days
 *
 * Also wires up the SW message bridge so an in-app sound plays when a push
 * arrives while the app is in foreground.
 */
export default function PushPermissionBanner() {
  const { user } = useAuth();
  const { theme, dir } = useI18n();
  const [permission, setPermission] = useState("unsupported");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    if (!user) return;
    setPermission(getPermissionStatus());
    const dismissedAt = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
    if (dismissedAt && Date.now() - dismissedAt < DISMISS_TTL_MS) setHidden(true);

    // Auto-resubscribe: if browser permission is already granted, make sure
    // the backend has a matching PushSubscription record. This recovers from:
    //   • Pruned subscription on server (404/410 cleanup)
    //   • Re-installed service worker / new device / new browser profile
    //   • User who granted before this fix was deployed
    if (isPushSupported() && Notification.permission === "granted") {
      (async () => {
        try {
          await subscribeUserToPush();
        } catch { /* keep silent — banner handles errors */ }
      })();
    }
  }, [user]);

  // Wire up SW push bridge — play sound + handle navigation
  useEffect(() => {
    const detach = attachPushBridge(
      () => { playNotificationSound(); },
      (link) => { if (link) window.location.href = link; },
    );
    return detach;
  }, []);

  if (!user || user.user_type !== "internal") return null;
  if (!isPushSupported()) return null;
  if (permission === "granted" || permission === "denied") return null;
  if (hidden) return null;

  const enable = async () => {
    setBusy(true); setError("");
    try {
      await subscribeUserToPush();
      setPermission("granted");
    } catch (e) {
      setError(e?.message || "تعذّر تفعيل التنبيهات");
      setPermission(getPermissionStatus());
    } finally { setBusy(false); }
  };

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setHidden(true);
  };

  return (
    <div
      dir={dir}
      role="region"
      aria-label="تفعيل التنبيهات"
      data-testid="push-permission-banner"
      style={{
        position: "fixed",
        bottom: 18,
        insetInlineStart: 18,
        insetInlineEnd: 18,
        maxWidth: 540,
        margin: "0 auto",
        background: `linear-gradient(135deg, ${theme.gold}, #b8902a)`,
        color: "#0a0a0a",
        padding: "14px 18px",
        borderRadius: 16,
        boxShadow: "0 12px 40px rgba(212,175,55,0.35), 0 4px 12px rgba(0,0,0,0.25)",
        display: "flex",
        alignItems: "center",
        gap: 14,
        zIndex: 9999,
        animation: "vtaSlideUp 320ms ease",
      }}
    >
      <style>{`@keyframes vtaSlideUp{from{transform:translateY(40px);opacity:0}to{transform:translateY(0);opacity:1}}`}</style>
      <Bell size={22} strokeWidth={2.6} style={{ flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 900, fontSize: 14 }}>تفعيل التنبيهات الفورية</div>
        <div style={{ fontSize: 12, opacity: 0.85, marginTop: 2 }}>
          استلم إشعارات المعاملات والمدفوعات فور حدوثها — حتى لو التطبيق مقفل.
        </div>
        {error && <div style={{ fontSize: 11, marginTop: 4, color: "#7f1d1d" }}>{error}</div>}
      </div>
      <button
        onClick={enable} disabled={busy}
        data-testid="push-enable-btn"
        style={{
          background: "#0a0a0a", color: theme.gold, border: "none",
          padding: "8px 14px", borderRadius: 999,
          fontWeight: 800, fontSize: 13, cursor: busy ? "wait" : "pointer",
          display: "inline-flex", alignItems: "center", gap: 6,
          flexShrink: 0,
        }}
      >
        {busy ? "…" : (<><Check size={14} strokeWidth={2.6} /> تفعيل</>)}
      </button>
      <button
        onClick={dismiss}
        aria-label="إغلاق"
        data-testid="push-dismiss-btn"
        style={{
          background: "transparent", border: "none", color: "#0a0a0a",
          cursor: "pointer", padding: 4, display: "inline-flex",
        }}
      >
        <X size={18} strokeWidth={2.4} />
      </button>
    </div>
  );
}

/** Smart single-button for users:
 *
 *  • If push permission isn't granted yet → "تفعيل الفحص" → subscribe + send test
 *  • If granted → "إرسال إشعار تجريبي" → fires a test push + in-app notif
 *  • If denied at the browser level → "مرفوض من المتصفح" (disabled, hint)
 *
 *  Replaces the old enable/disable toggle. One button to rule them all
 *  so users don't have to think about subscribing vs testing.
 */
export function PushToggle() {
  const { theme, lang } = useI18n();
  const [status, setStatus] = useState("unsupported");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  useEffect(() => { setStatus(getPermissionStatus()); }, []);

  const T = lang === "ar" ? {
    not_supported: "متصفحك لا يدعم تنبيهات الويب.",
    failed: "تعذّر الفحص",
    denied: "مرفوض من المتصفح — افتح الإعدادات",
    enable_and_test: "تفعيل + إرسال إشعار تجريبي",
    test_again: "إرسال إشعار تجريبي",
    busy: "جارٍ الإرسال…",
    delivered: (n) => n > 0
      ? `✓ وصل لـ ${n} ${n === 1 ? "جهاز" : "أجهزة"} — تحقّق من شاشة القفل وعداد الجرس`
      : "✓ تم إنشاء إشعار داخل التطبيق — للتجربة على الجوال فعّل الاشتراك في هذا الجهاز.",
  } : {
    not_supported: "Your browser doesn't support web push.",
    failed: "Test failed",
    denied: "Blocked by browser — open settings",
    enable_and_test: "Enable + send test",
    test_again: "Send a test notification",
    busy: "Sending…",
    delivered: (n) => n > 0
      ? `✓ delivered to ${n} device${n === 1 ? "" : "s"} — check lock screen + bell counter`
      : "✓ in-app notification created — enable on this device for lock-screen alerts.",
  };

  if (!isPushSupported()) {
    return <div style={{ color: theme.textMuted, fontSize: 12 }}>{T.not_supported}</div>;
  }

  const run = async () => {
    setBusy(true); setErr(""); setMsg("");
    try {
      // 1. Ensure subscribed (no-op if permission already granted + subscription exists)
      if (status !== "granted") {
        await subscribeUserToPush();
        setStatus(getPermissionStatus());
      }
      // 2. Fire the self-test (always — even if already subscribed, lets user re-verify)
      const { data } = await api.post("/push/test");
      setMsg(T.delivered(Number(data?.delivered || 0)));
    } catch (e) {
      setErr(e?.message || e?.response?.data?.detail || T.failed);
      setStatus(getPermissionStatus());
    } finally { setBusy(false); }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "flex-end" }} data-testid="push-toggle">
      <button
        onClick={run}
        disabled={busy || status === "denied"}
        data-testid="push-test-btn"
        style={{
          ...btn(theme),
          background: status === "denied" ? "#7f1d1d" : theme.gold,
          color: status === "denied" ? "#fff" : "#0a0a0a",
          opacity: busy ? 0.7 : 1,
        }}
      >
        <Bell size={13} strokeWidth={2.4} />
        {busy
          ? T.busy
          : status === "denied"
            ? T.denied
            : status === "granted"
              ? T.test_again
              : T.enable_and_test}
      </button>
      {msg && <span style={{ color: "#16a34a", fontSize: 10, fontWeight: 700 }}>{msg}</span>}
      {err && <span style={{ color: "#dc2626", fontSize: 10 }}>{err}</span>}
    </div>
  );
}

const btn = (theme) => ({
  border: "none", padding: "8px 14px", borderRadius: 10,
  fontWeight: 800, fontSize: 13, cursor: "pointer",
  display: "inline-flex", alignItems: "center", gap: 6,
});
